import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/roles';
import './InventoryPage/InventoryPage.css';

const STATUS_LABEL = {
  abierta: 'Abierta',
  en_reparacion: 'En reparación',
  resuelta: 'Resuelta',
};

const STATUS_BADGE = {
  abierta: 'badge-red',
  en_reparacion: 'badge-yellow',
  resuelta: 'badge-green',
};

const TYPE_LABEL = {
  item: 'Artículo',
  rack: 'Rack',
  access_pack: 'Pack',
};

function normalize(value = '') {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

// Badge de estado clickeable: abre un men\u00fa para pasar la incidencia a
// abierta / en reparaci\u00f3n / resuelta (reabrir incluido).
function StatusBadgePicker({ incident, canManage, onChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  function toggle(e) {
    e.stopPropagation();
    if (!canManage) return;
    if (!open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={`badge ${STATUS_BADGE[incident.status]}`}
        style={canManage ? { cursor: 'pointer' } : undefined}
        title={canManage ? 'Cambiar estado' : undefined}
        onClick={toggle}
      >
        {STATUS_LABEL[incident.status]}
      </span>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            padding: 4, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 150,
          }}
        >
          {['abierta', 'en_reparacion', 'resuelta'].map(s => (
            <button
              key={s}
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ justifyContent: 'flex-start', display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => { setOpen(false); if (s !== incident.status) onChange(s); }}
            >
              <span className={`badge ${STATUS_BADGE[s]}`}>{STATUS_LABEL[s]}</span>
              {incident.status === s && <span>\u2713</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function equipmentLabel(item) {
  return `${item.name}${item.unit_label ? ` · ${item.unit_label}` : ''}`;
}

function NewIncidentModal({ equipment, onClose, onSaved }) {
  const [targetId, setTargetId] = useState('');
  const [description, setDescription] = useState('');
  const [canOperate, setCanOperate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selected = equipment.find(item => `${item.target_type}:${item.id}` === targetId);

  const submit = async event => {
    event.preventDefault();
    if (!selected) return setError('Elegí un artículo, rack o pack.');
    const targetField = selected.target_type === 'item'
      ? 'item_id'
      : selected.target_type === 'rack'
        ? 'pack_id'
        : 'access_pack_id';
    setSaving(true);
    setError('');
    try {
      await api.post('/inventory/incidents', {
        [targetField]: selected.id,
        description,
        can_operate: canOperate,
      });
      onSaved();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'No se pudo crear la incidencia.');
    } finally {
      setSaving(false);
    }
  };

  const grouped = type => equipment.filter(item => item.target_type === type);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal inv-incident-modal" onClick={event => event.stopPropagation()} onSubmit={submit}>
        <div className="modal-title">Nueva incidencia</div>
        <p className="text-dim text-sm">Registrá el problema sobre un artículo, rack o pack.</p>
        {error && <div className="inv-error">{error}</div>}
        <div className="form-group">
          <label className="form-label">Elemento</label>
          <select className="input" value={targetId} onChange={event => setTargetId(event.target.value)} required>
            <option value="">Seleccionar...</option>
            {['item', 'rack', 'access_pack'].map(type => grouped(type).length > 0 && (
              <optgroup key={type} label={TYPE_LABEL[type]}>
                {grouped(type).map(item => (
                  <option key={`${type}-${item.id}`} value={`${type}:${item.id}`}>
                    {item.code ? `${item.code} · ` : ''}{equipmentLabel(item)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Descripción</label>
          <textarea className="input" rows="4" value={description} onChange={event => setDescription(event.target.value)} placeholder="¿Qué pasó?" required />
        </div>
        <label className="inv-checkbox-label">
          <input type="checkbox" checked={canOperate} onChange={event => setCanOperate(event.target.checked)} />
          Puede seguir operando
        </label>
        <div className="modal-actions">
          <button className="btn btn-ghost" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Crear incidencia'}</button>
        </div>
      </form>
    </div>
  );
}

export default function InventoryIncidentsPage() {
  const { user } = useAuth();
  const canManage = canWrite(user, 'inventario.incidencias');
  const [incidents, setIncidents] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('abiertas');
  const [typeFilter, setTypeFilter] = useState('todas');
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Cargas independientes: si falla el catálogo de equipos (formulario),
      // la lista de incidencias se muestra igual — antes un solo 500 dejaba
      // la pestaña vacía en silencio.
      const [incidentsResult, equipmentResult] = await Promise.allSettled([
        api.get('/inventory/incidents'),
        api.get('/inventory/incidents/equipment'),
      ]);
      const errors = [];
      if (incidentsResult.status === 'fulfilled') setIncidents(incidentsResult.value.data);
      else errors.push('la lista de incidencias');
      if (equipmentResult.status === 'fulfilled') setEquipment(equipmentResult.value.data);
      else errors.push('el catálogo de equipos del formulario');
      setLoadError(errors.length ? `No se pudo cargar ${errors.join(' ni ')}.` : '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const visibleIncidents = useMemo(() => {
    const query = normalize(search);
    return incidents.filter(incident => {
      const hasStatus = statusFilter === 'todas'
        || (statusFilter === 'abiertas' ? incident.status !== 'resuelta' : incident.status === statusFilter);
      const hasType = typeFilter === 'todas' || incident.equipment_type === typeFilter;
      const haystack = normalize(`${incident.equipment_code || ''} ${incident.equipment_name || ''} ${incident.equipment_label || ''} ${incident.description || ''}`);
      return hasStatus && hasType && (!query || haystack.includes(query));
    });
  }, [incidents, search, statusFilter, typeFilter]);

  const updateStatus = async (id, status) => {
    await api.put(`/inventory/incidents/${id}`, { status });
    load();
  };

  return (
    <div className="inventory-page inventory-incidents-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Incidencias</h1>
          <p className="page-subtitle">Estado operativo de artículos, racks y packs</p>
        </div>
        {canManage && <button className="btn btn-primary inv-add-btn" onClick={() => setShowForm(true)}>+ Nueva incidencia</button>}
      </div>

      <div className="inv-toolbar">
        <div className="inv-search-wrapper">
          <span className="inv-search-icon">⌕</span>
          <input className="input" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar por código, elemento o descripción..." />
        </div>
        <div className="inv-category-tabs">
          {[
            ['abiertas', 'Abiertas'],
            ['en_reparacion', 'En reparación'],
            ['resuelta', 'Resueltas'],
            ['todas', 'Todas'],
          ].map(([value, label]) => (
            <button key={value} className={`btn btn-ghost btn-sm${statusFilter === value ? ' active-tab' : ''}`} onClick={() => setStatusFilter(value)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="inv-category-tabs inv-incident-type-tabs">
        {[
          ['todas', 'Todos'],
          ['item', 'Artículos'],
          ['rack', 'Racks'],
          ['access_pack', 'Packs'],
        ].map(([value, label]) => (
          <button key={value} className={`btn btn-ghost btn-sm${typeFilter === value ? ' active-tab' : ''}`} onClick={() => setTypeFilter(value)}>{label}</button>
        ))}
      </div>

      {loadError && <div className="inv-table-empty" style={{ color: 'var(--red)' }}>{loadError}</div>}
      {loading ? <div className="inv-table-empty">Cargando incidencias...</div> : (
        <div className="inv-table inv-incidents-table">
          <div className="inv-table-head inv-incidents-table-head">
            <span>Código</span><span>Tipo</span><span>Elemento</span><span>Incidencia</span><span>Estado</span><span>Operación</span><span>Reportada</span>{canManage && <span />}
          </div>
          {visibleIncidents.map(incident => (
            <div className="inv-table-row inv-incidents-table-row" key={incident.id}>
              <span className="inv-code">{incident.equipment_code || '—'}</span>
              <span>{TYPE_LABEL[incident.equipment_type] || '—'}</span>
              <strong>{incident.equipment_name}{incident.equipment_label ? ` · ${incident.equipment_label}` : ''}</strong>
              <span className="inv-table-notes">{incident.description}</span>
              <span><StatusBadgePicker incident={incident} canManage={canManage} onChange={s => updateStatus(incident.id, s)} /></span>
              <span>{incident.can_operate ? 'Puede operar' : 'Fuera de servicio'}</span>
              <span>{new Date(incident.reported_at).toLocaleDateString('es-UY')}</span>
              {canManage && <div className="inv-table-actions">
                {incident.status === 'abierta' && <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(incident.id, 'en_reparacion')}>Reparar</button>}
                {incident.status !== 'resuelta' && <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(incident.id, 'resuelta')}>Resolver</button>}
              </div>}
            </div>
          ))}
          {!visibleIncidents.length && <div className="inv-table-empty">No hay incidencias para estos filtros.</div>}
        </div>
      )}
      {showForm && <NewIncidentModal equipment={equipment} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
